import React from 'react';
import { Box, ListItem, Typography, useMediaQuery } from '@mui/material';

import type { FileUploadType } from '../../../features/file-upload';
import { SkeletonWrapper } from '../../layout/index.js';
import type { DocumentFromStorage } from '../DocumentManager.types.ts';
import { ActionButtons } from './ActionButtons.tsx';

const throwNever = (value: never): never => {
  throw new Error(`Unexpected value: ${value}`);
};

const getOnText = (type: FileUploadType) => {
  switch (type) {
    case 'PATIENT_DOCUMENT_UPLOAD':
      return 'Uploaded on';
    case 'CANDOR_DOCUMENT_UPLOAD':
      return 'Uploaded on';
    case 'CONSENT_TO_RELEASE_HEALTH_SUMMARY':
      return 'Generated on';
    default:
      return throwNever(type);
  }
};

type UploadedAtSectionProps = {
  isLoading: boolean;
  document: DocumentFromStorage | null;
};

const UploadedAtSection = ({ isLoading, document }: UploadedAtSectionProps) => {
  if (isLoading) {
    return <Typography variant="caption">Loading uploaded at...</Typography>;
  }

  if (!document?.uploadedAt) {
    return null;
  }

  const onText = getOnText(document.type);

  return (
    <Typography variant="caption">
      {onText}{' '}
      {document.uploadedAt.toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      })}
    </Typography>
  );
};

interface DocumentListItemProps extends React.ComponentProps<typeof ListItem> {
  isLoading: boolean;
  document: DocumentFromStorage | null;
  onDocumentDownload: React.ComponentProps<typeof ActionButtons>['onDocumentDownload'];
  onPreview: React.ComponentProps<typeof ActionButtons>['onPreview'];
}

export const DocumentListItem = ({
  isLoading,
  document,
  onDocumentDownload,
  onPreview,
  ...props
}: DocumentListItemProps) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  return (
    <ListItem
      {...props}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexDirection: 'column',
        py: 0,
        px: 0,
        gap: 1,
        '& .MuiListItemSecondaryAction-root': {
          right: 0,
          padding: 'inherit',
        },
        ...props.sx,
      }}
    >
      {isMobile && (
        <SkeletonWrapper isLoading={isLoading}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500, alignSelf: 'flex-start' }}>
            {document?.fileName}
            {isLoading && 'Loading Document Name...'}
          </Typography>
        </SkeletonWrapper>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!isMobile && (
            <SkeletonWrapper isLoading={isLoading}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {document?.fileName}
                {isLoading && 'Loading Document Name...'}
              </Typography>
            </SkeletonWrapper>
          )}

          <SkeletonWrapper isLoading={isLoading}>
            <Typography variant="body2">{document?.description || 'No description provided'}</Typography>
          </SkeletonWrapper>

          <SkeletonWrapper isLoading={isLoading} width={'200px'}>
            <UploadedAtSection isLoading={isLoading} document={document} />
          </SkeletonWrapper>
        </Box>
        <ActionButtons
          document={document}
          isLoading={isLoading}
          onDocumentDownload={onDocumentDownload}
          onPreview={onPreview}
        />
      </Box>
    </ListItem>
  );
};
